import React from 'react';
import { createStackNavigator } from '@react-navigation/stack';
import { useTranslation } from 'react-i18next';
import useTheme from '../../hooks/useTheme';
import { getStackScreenOptions } from '../config/screenOptions';
import { DashboardStackParamList } from '../types/navigation.types';
import { createLazyScreen } from '../utils/lazyScreen';
import { SCREEN_TITLES } from '../config/constants';
import { StackErrorBoundary } from '../components/StackErrorBoundary';

// Lazy load screens
const DashboardScreen = createLazyScreen(() => import('../../screens/dashboard/dashboard-screen'));
const DataMigrationScreen = createLazyScreen(
  () => import('../../screens/admin/data-migration-screen'),
);
const AIInsightsDashboardScreen = createLazyScreen(
  () => import('../../screens/insights/ai-insights-dashboard-screen'),
);
const NotificationCenterScreen = createLazyScreen(
  () => import('../../screens/notifications/notification-center-screen'),
);
const ExportInvoicesScreen = createLazyScreen(
  () => import('../../screens/export/export-invoices-screen'),
);

// Budget Management Screens
const BudgetDashboardScreen = createLazyScreen(
  () => import('../../screens/enterprise/budget/budget-dashboard-screen'),
);
const BudgetListScreen = createLazyScreen(
  () => import('../../screens/enterprise/budget/budget-list-screen'),
);
const BudgetDetailScreen = createLazyScreen(
  () => import('../../screens/enterprise/budget/budget-detail-screen'),
);
const BudgetFormScreen = createLazyScreen(
  () => import('../../screens/enterprise/budget/budget-form-screen'),
);
const BudgetVarianceScreen = createLazyScreen(
  () => import('../../screens/enterprise/budget/budget-variance-screen'),
);

const Stack = createStackNavigator<DashboardStackParamList>();

export function DashboardStack() {
  const { t } = useTranslation();
  const { colors } = useTheme();

  return (
    <StackErrorBoundary stackName="Dashboard">
      <Stack.Navigator screenOptions={getStackScreenOptions(colors)}>
        <Stack.Screen
          name="DashboardHome"
          component={DashboardScreen}
          options={{ headerShown: false }}
        />
        <Stack.Screen
          name="DataMigration"
          component={DataMigrationScreen}
          options={{ title: t(SCREEN_TITLES.DATA_MIGRATION, 'Data Migration') }}
        />
        <Stack.Screen
          name="AIInsights"
          component={AIInsightsDashboardScreen}
          options={{ title: t('insights.title', 'AI Insights') }}
        />
        <Stack.Screen
          name="NotificationCenter"
          component={NotificationCenterScreen}
          options={{ title: t('notifications.title', 'Notifications') }}
        />
        <Stack.Screen
          name="ExportInvoices"
          component={ExportInvoicesScreen}
          options={{ title: t('export.title', 'Export Data') }}
        />
        {/* Budget Management Screens */}
        <Stack.Screen
          name="BudgetDashboard"
          component={BudgetDashboardScreen}
          options={{ title: t('budget.dashboard', 'Budget Dashboard') }}
        />
        <Stack.Screen
          name="BudgetList"
          component={BudgetListScreen}
          options={{ title: t('budget.list', 'Budgets') }}
        />
        <Stack.Screen
          name="BudgetDetail"
          component={BudgetDetailScreen}
          options={{ title: t('budget.detail', 'Budget Details') }}
        />
        <Stack.Screen
          name="BudgetForm"
          component={BudgetFormScreen}
          options={{ title: t('budget.form', 'Budget Form') }}
        />
        <Stack.Screen
          name="BudgetVariance"
          component={BudgetVarianceScreen}
          options={{ title: t('budget.variance', 'Variance Analysis') }}
        />
      </Stack.Navigator>
    </StackErrorBoundary>
  );
}
